import {
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { reviewTokens } from "../theme";

export interface LazyThumbProps {
  src?: string;
  alt?: string;
  /** Square size (px) or explicit width/height. Ignored when aspectRatio set. */
  size?: number | { width: number; height: number };
  /** CSS object-fit for the image. Defaults to "cover". */
  fit?: "cover" | "contain";
  /** Border radius in px. */
  radius?: number;
  /** Responsive box: width 100% with this width/height ratio (e.g. 1 = 1:1). */
  aspectRatio?: number;
  /** Rendered when there is no src or the image fails to load. */
  fallback?: ReactNode;
  /** How far outside the viewport to start loading. Defaults to "100px". */
  rootMargin?: string;
  "data-testid"?: string;
}

/**
 * Viewport-lazy image thumbnail.
 *
 * Reuses the same IntersectionObserver + fade-in approach as the app-wide
 * `LazyImage` component, but is size-configurable. `LazyImage` hard-codes a
 * 136px / object-fit:contain image (see its styled.ts) which cannot be
 * overridden via props, so using it directly here would visually break every
 * thumbnail. `LazyThumb` keeps the lazy-loading UX while preserving each call
 * site's exact dimensions, fit, and radius.
 */
export function LazyThumb({
  src,
  alt = "",
  size,
  fit = "cover",
  radius,
  aspectRatio,
  fallback,
  rootMargin = "100px",
  "data-testid": dataTestId,
}: LazyThumbProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  // Reset load state when the source changes.
  useEffect(() => {
    setLoaded(false);
    setErrored(false);
  }, [src]);

  useEffect(() => {
    if (visible) return;
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      // jsdom / unsupported environments: load immediately.
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  const dims =
    typeof size === "number"
      ? { width: size, height: size }
      : size
        ? { width: size.width, height: size.height }
        : undefined;

  const showFallback = !src || errored;

  return (
    <Box
      ref={containerRef}
      data-testid={dataTestId}
      sx={{
        position: "relative",
        flexShrink: 0,
        overflow: "hidden",
        borderRadius: radius != null ? `${radius}px` : undefined,
        bgcolor: reviewTokens.slate100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(aspectRatio
          ? { width: "100%", aspectRatio: `${aspectRatio}` }
          : dims
            ? { width: dims.width, height: dims.height }
            : { width: "100%", height: "100%" }),
      }}
    >
      {showFallback
        ? fallback ?? null
        : visible && (
            <Box
              component="img"
              src={src}
              alt={alt}
              onLoad={() => setLoaded(true)}
              onError={() => setErrored(true)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: fit,
                display: "block",
                opacity: loaded ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
              }}
            />
          )}
    </Box>
  );
}
